import axios from 'axios';

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api';

export const api = axios.create({
  baseURL,
  withCredentials: false,
  timeout: 15_000,
});

// ── In-memory token store ────────────────────────────────────────────
// The interceptor reads from these module-level vars (NOT from
// localStorage on every request) so AuthProvider can set/clear tokens
// synchronously and there's no per-request disk read race.
let accessToken = null;
let refreshToken = null;
let onUnauthorized = null;

/**
 * Set both tokens at once, persist to localStorage, and broadcast to the
 * Axios interceptor. Call with `null` to clear.
 */
export function setTokens(next) {
  accessToken = next?.accessToken ?? null;
  refreshToken = next?.refreshToken ?? null;
  if (accessToken) localStorage.setItem('bos.access', accessToken);
  else localStorage.removeItem('bos.access');
  if (refreshToken) localStorage.setItem('bos.refresh', refreshToken);
  else localStorage.removeItem('bos.refresh');
}

/**
 * Set just the access token in the in-memory store (e.g. when the
 * AuthProvider rehydrates from localStorage after JWT-exp validation).
 * Mirrors the spec's `setApiToken` API name so AuthProvider can call it
 * imperatively without going through setTokens / writing to storage.
 */
export function setApiToken(token) {
  accessToken = token ?? null;
}

/**
 * Read tokens out of localStorage into the in-memory store.
 * AuthProvider calls this once during bootstrap. Returns the loaded pair.
 */
export function loadStoredTokens() {
  accessToken = localStorage.getItem('bos.access');
  refreshToken = localStorage.getItem('bos.refresh');
  return { accessToken, refreshToken };
}

export function setOnUnauthorized(cb) {
  onUnauthorized = cb;
}

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

let refreshing = null;

api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry && refreshToken) {
      original._retry = true;
      try {
        refreshing =
          refreshing ||
          axios
            .post(`${baseURL}/auth/refresh`, { refreshToken })
            .then((r) => r.data?.data)
            .finally(() => {
              refreshing = null;
            });
        const next = await refreshing;
        setTokens(next);
        original.headers.Authorization = `Bearer ${next.accessToken}`;
        return api(original);
      } catch (refreshErr) {
        setTokens(null);
        if (onUnauthorized) onUnauthorized();
        return Promise.reject(refreshErr);
      }
    }
    return Promise.reject(error);
  }
);

// Default export so `import api from './client'` (used by all BOS+ plus pages)
// resolves to the same axios instance as the named `api` export.
export default api;
